import { useState } from 'react'
import { QrCode, Smartphone, X } from 'lucide-react'
import { detectCaptureClient } from '@/lib/capture-heartbeat'
import { APP_STORE_URL } from '@/lib/app-store'
import { useAuthStore } from '@/stores/auth'

/**
 * "Get Claro on your iPhone" for a clinician using Claro on a computer who
 * has never used the app.
 *
 * Dr Chad Carman accepted his invite on the web (2026-09-23), was never asked
 * for a password — invites don't need one — and could not get into the iPhone
 * app (2026-10-01). The app signs in with an emailed link too, but nothing on
 * the web said so, and the empty-state link to the App Store is gone after the
 * first note. He wrote two notes and stopped.
 *
 * Shown only on a computer (an iPhone browser gets IosBrowserRecordingNotice),
 * only when /auth/me says `has_ios_app: false` (an older server that omits the
 * field shows nothing rather than guessing), and never again once dismissed:
 * it is a pointer, not a reminder.
 */
const DISMISSED_KEY = 'claro.get-the-app-notice.dismissed'

function dismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1'
  } catch {
    return false
  }
}

export function GetTheAppNotice() {
  const hasIosApp = useAuthStore(
    (s) => (s.user as { has_ios_app?: boolean } | null)?.has_ios_app,
  )
  const [hidden, setHidden] = useState(() => detectCaptureClient() !== 'web_desktop' || dismissed())
  const [qr, setQr] = useState<string | null>(null)

  if (hidden || hasIosApp !== false) return null

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, '1')
    } catch {
      /* private mode — session-only dismissal is fine */
    }
    setHidden(true)
  }

  const toggleQr = async () => {
    if (qr) return setQr(null)
    try {
      const QRCode = await import('qrcode')
      setQr(await QRCode.toDataURL(APP_STORE_URL, { width: 320, margin: 1 }))
    } catch {
      /* QR unavailable — the App Store link still works */
    }
  }

  return (
    <div
      role="status"
      data-testid="get-the-app-notice"
      className="shrink-0 bg-claro-50 border-b border-claro-200 px-4 py-2"
    >
      <div className="flex items-center gap-3">
        <Smartphone className="w-4 h-4 text-claro-700 shrink-0" aria-hidden />
        <p className="flex-1 text-[13px] text-ink-secondary">
          <span className="font-medium text-ink">Get Claro on your iPhone.</span> Sign in with your
          email the first time, then use Face ID.{' '}
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium link"
          >
            App Store
          </a>
          {' · '}
          <button
            type="button"
            onClick={toggleQr}
            aria-expanded={!!qr}
            className="inline-flex items-center gap-1 font-medium text-claro-700 underline underline-offset-2"
          >
            <QrCode className="w-3.5 h-3.5" aria-hidden />
            {qr ? 'Hide QR code' : 'Scan with your phone'}
          </button>
        </p>
        <button
          onClick={dismiss}
          className="p-1 rounded text-ink-faint hover:text-ink hover:bg-surface-tertiary transition-colors shrink-0"
          aria-label="Dismiss"
          title="Dismiss"
        >
          <X className="w-3.5 h-3.5" aria-hidden />
        </button>
      </div>
      {qr && (
        <img
          src={qr}
          alt="QR code for Claro on the App Store"
          className="mt-2 ml-7 w-32 h-32 rounded border border-claro-200 bg-white"
        />
      )}
    </div>
  )
}
